/**
 * Tracks the network status (online / offline / syncing) and exposes
 * a manual retry trigger. Navigator.onLine alone is unreliable — we
 * also ping Supabase periodically when it reports offline.
 */
import { useState, useEffect, useCallback, useRef } from 'react'
import { SUPABASE_CONFIG } from '@/utils/constants'

export type NetworkState = 'online' | 'offline' | 'syncing'

export function useNetworkStatus() {
  const [state, setState] = useState<NetworkState>(() =>
    typeof navigator !== 'undefined' && navigator.onLine ? 'online' : 'offline'
  )
  const pingTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const pingServer = useCallback(async (): Promise<boolean> => {
    if (!SUPABASE_CONFIG.url) return false
    try {
      const controller = new AbortController()
      const t = setTimeout(() => controller.abort(), 5000)
      const res = await fetch(`${SUPABASE_CONFIG.url}/rest/v1/`, {
        method: 'HEAD',
        signal: controller.signal,
        headers: { apikey: SUPABASE_CONFIG.anonKey },
      })
      clearTimeout(t)
      return res.ok || res.status === 404 // 404 is fine, means server alive
    } catch {
      return false
    }
  }, [])

  const checkNow = useCallback(async () => {
    const ok = await pingServer()
    setState(ok ? 'online' : 'offline')
    return ok
  }, [pingServer])

  useEffect(() => {
    const onOnline = () => setState('online')
    const onOffline = () => setState('offline')
    window.addEventListener('online', onOnline)
    window.addEventListener('offline', onOffline)

    // Periodic probe when browser thinks we're offline
    const startProbe = () => {
      if (pingTimer.current) clearTimeout(pingTimer.current)
      pingTimer.current = setTimeout(async () => {
        const ok = await pingServer()
        if (ok) setState('online')
        else startProbe()
      }, 30_000)
    }

    if (!navigator.onLine) startProbe()

    return () => {
      window.removeEventListener('online', onOnline)
      window.removeEventListener('offline', onOffline)
      if (pingTimer.current) clearTimeout(pingTimer.current)
    }
  }, [pingServer])

  // Listen to sync events from offlineQueue
  useEffect(() => {
    const onStart = () => setState(prev => (prev === 'online' ? 'syncing' : prev))
    const onEnd = () => setState(prev => (prev === 'syncing' ? 'online' : prev))
    window.addEventListener('offline-sync-started', onStart)
    window.addEventListener('offline-sync-completed', onEnd)
    return () => {
      window.removeEventListener('offline-sync-started', onStart)
      window.removeEventListener('offline-sync-completed', onEnd)
    }
  }, [])

  return {
    state,
    isOnline: state === 'online',
    isOffline: state === 'offline',
    isSyncing: state === 'syncing',
    checkNow,
  }
}
